import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {UnmountClosed} from 'react-collapse';
import {Button, Space} from 'antd';
import {
  StyledUserReview,
  StyledUserReviewActions,
  StyledUserReviewUserDetails,
  StyledUserReviewDetails,
  StyledUserReviewDetailsHeader,
  StyledUserReviewStatusDetails,
  StyledUserReviewMessage,
  StyledUserReviewDetailsEditButton,
  StyledUserReviewCollapseIcon
} from './StyledUserReview';
import {CheckCircleIcon, CheckIcon, ChevronDownIcon, CloseCircleIcon, CloseIcon, StarsIcon} from '../../icons';
import IconWrapper from '../IconWrapper';
import UserSelectDropdown from '../UserSelectDropdown';
import {colorsStyles} from '../../utils/styles';

const ReviewButtons = ({ approveButtonProps, rejectButtonProps, ...rest } = {}) => {
  const [t] = useTranslation('main');

  const checkShowProp = (props) => props && props.hasOwnProperty('show') ? props.show : true;

  const showApprove = checkShowProp(approveButtonProps);
  const showReject = checkShowProp(rejectButtonProps);

  return (
    <StyledUserReviewActions single={!showApprove || !showReject} {...rest}>
      {showReject && (
        <Button icon={<CloseIcon />} size='small' {...rejectButtonProps}>
          {rejectButtonProps.text || t('reject')}
        </Button>
      )}
      {showApprove && (
        <Button icon={<CheckIcon />} size='small' type='primary' {...approveButtonProps}>
          {approveButtonProps.text || t('approve')}
        </Button>
      )}
    </StyledUserReviewActions>
  );
};


const UserReview = ({
  approveButtonProps,
  canEdit = false,
  canReassign = false,
  commentType = 'visible',
  date,
  message,
  name,
  onApprove,
  onEdit,
  onReject,
  onReassign,
  reviewer,
  reassignButtonText,
  rejectButtonProps,
  status = 'default',
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [showComment, setShowComment] = useState(false);

  const isRejected = status === 'rejected';
  const isPending = status === 'pending';
  const isApproved = status === 'approved';
  const isAutoApproval = status === 'auto-approval';

  const getUserReviewDetails = () => {
    const avatarProps = { size: 16 };
    const props = { avatarProps, email: false, user: reviewer };
    if (isAutoApproval) {
      props.customAvatar = (
        <IconWrapper
          {...avatarProps}
          size={12}
          colorMode='stroke'
          color={colorsStyles.additional_purple}
          style={{backgroundColor: colorsStyles.additional_purple_light, borderRadius: '50%', width: 16, height: 16 }}
        >
          <StarsIcon />
        </IconWrapper>
      );
    }
    return <StyledUserReviewUserDetails {...props} />;
  }

  const getReviewStatusDetails = () => {
    if (isAutoApproval) {
      return (
        <Space size='small' align='center'>
          <CheckCircleIcon />
          <span>{t(`expenseReviewStatuses.approved`)}</span>
        </Space>
      );
    }
    if (isApproved) {
      return (
        <Space size='small' align='center'>
          <CheckCircleIcon />
          <span>{t(`expenseReviewStatuses.${status}`)}, {date}</span>
        </Space>
      );
    }
    if (isRejected) {
      return (
        <Space size='small' align='center'>
          <CloseCircleIcon />
          <span>{t(`expenseReviewStatuses.${status}`)}, {date}</span>
        </Space>
      );
    }
    return null;
  }

  const isEnabledReassign = canReassign && isPending;

  const isEnabledReview = isApproved || isRejected || isAutoApproval;

  return (
    <StyledUserReview
      status={status}
      {...rest}
    >
      <StyledUserReviewDetails>
        <StyledUserReviewDetailsHeader>
          {name && (
            <Space size='small' className='d-flex-child'>
              {/* show collapse/expand button next to the name when commentType is collapsed and message exists */}
              {commentType === 'collapsed' && message && (
                <StyledUserReviewCollapseIcon
                  rotate={`${showComment ? '-180deg' : '0deg'}`}
                  size={16}
                  onClick={() => setShowComment(prev => !prev)}
                >
                  <ChevronDownIcon />
                </StyledUserReviewCollapseIcon>
              )}
              <strong>{name}</strong>
            </Space>
          )}
          <Space size='small' className='d-flex-child'>
            {getUserReviewDetails()}
            {isEnabledReassign && (
              <UserSelectDropdown
                arrow={true}
                hideSelected={true}
                listPropName='email'
                onlyAdmins={true}
                onSelect={onReassign}
                placement='bottomRight'
                selectedValue={reviewer.employee_id}
              >
                <StyledUserReviewDetailsEditButton type='link'>
                  {reassignButtonText || t('reassign')}
                </StyledUserReviewDetailsEditButton>
              </UserSelectDropdown>
            )}
          </Space>
        </StyledUserReviewDetailsHeader>

        {isEnabledReview && (
          <StyledUserReviewStatusDetails status={status}>
            {getReviewStatusDetails()}
            {canEdit && (
              <StyledUserReviewDetailsEditButton onClick={onEdit} type='link'>
                {t('edit')}
              </StyledUserReviewDetailsEditButton>
            )}
          </StyledUserReviewStatusDetails>
        )}
      </StyledUserReviewDetails>

      {/* visible comment (existing behavior) */}
      {(message && commentType === 'visible') && (
        <StyledUserReviewMessage status={status}>
          {message}
        </StyledUserReviewMessage>
      )}

      {/* collapsed comment: render before review buttons; toggled by the button placed next to the name */}
      {(commentType === 'collapsed' && message && showComment) && (
        <UnmountClosed isOpened={showComment}>
          <StyledUserReviewMessage status={status}>{message}</StyledUserReviewMessage>
        </UnmountClosed>
      )}

      {(isPending && canEdit) && (
        <ReviewButtons
          approveButtonProps={{onClick: onApprove, ...approveButtonProps}}
          rejectButtonProps={{onClick: onReject, ...rejectButtonProps}}
        />
      )}
    </StyledUserReview>
  );
}

UserReview.propTypes = {
  approveButtonProps: PropTypes.object,
  commentType: PropTypes.oneOf(['collapsed', 'visible']),
  onApprove: PropTypes.func,
  onEdit: PropTypes.func,
  onReject: PropTypes.func,
  onReassign: PropTypes.func,
  reassignButtonText: PropTypes.node,
  rejectButtonProps: PropTypes.object,
  status: PropTypes.oneOf(['approved', 'pending', 'rejected', 'default', 'auto-approval']),
}

UserReview.ReviewButtons = ReviewButtons;

export default UserReview;

